import { useQueries, useQuery } from "@tanstack/react-query";
import { useAuthFetch } from "../../../utils/useAuthFetch";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useRelationships } from "./useRelationships";
import { createViewService } from "../services/viewService";
import type { CanvasDataSource, SchemaTableWithColumns } from "../types";
/** Admin builder metadata only. Runtime widgets never call schema metadata routes. */
export function useHierarchyBuilderData(sources: CanvasDataSource[]) {
  const authFetch = useAuthFetch();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";
  const schema = useQuery({
    queryKey: ["hierarchyBuilderTables", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () =>
      (await authFetch(
        "/api/workspace/myvault/schemas/tables/full",
        "fetch hierarchy builder tables",
        { headers: { "x-workspace-id": workspaceId } },
      )) as SchemaTableWithColumns[],
  });
  const views = useQueries({
    queries: sources.map((source) => ({
      queryKey: ["hierarchyBuilderView", workspaceId, source.view_id],
      enabled: !!workspaceId && !!source.view_id,
      queryFn: () =>
        createViewService(authFetch, workspaceId).getView(
          String(source.view_id),
        ),
    })),
  });
  const relations = useRelationships(workspaceId);
  return {
    tables: schema.data || [],
    relationships: relations.relationships,
    views: Object.fromEntries(
      sources.map((source, index) => [source.id, views[index].data]),
    ),
    loading:
      schema.isLoading ||
      relations.loading ||
      views.some((view) => view.isLoading),
    error:
      schema.error ||
      relations.error ||
      views.find((view) => view.error)?.error,
  };
}
